import { WizardRecomendedControlsFormValues } from 'src/api/types/wizard';
import _ from 'lodash';
import { ControlsRmpQuery, FrameworkControlScenarioInput } from 'src/graphql/_generated';

export const SLIDE_HEIGHT = 400;

export const initialValues = {
  scenarios: {
    B2B: false,
    B2C: false,
    publiclyAccessibleInternetBusiness: false,
    paymentVolumeOrValue: false,
    significantITContributionToRevenue: false,
    significantDataInCloud: false,
    softwareBusiness: false,
    cniOrDefenseInvolvement: false,
    significantRandD: false,
    regulated: false,
    operationalTechnology: false,
    supplyChainReliant: false,
    aiThirdPartyReliant: false,
    aiCustomerFacingApp: false,
  },
  selectedControls: {} as Record<string, Record<string, boolean>>, // controlId -> scenarioId -> isSelected
  addRecommendedModifiers: false,
} satisfies WizardRecomendedControlsFormValues;

export const getControlId = (control: any): string => {
  return control.id || control.ux_id;
};

export const hasNewSelectedControls = (
  selectedControls: Record<string, Record<string, boolean>>,
  rmpControls: ControlsRmpQuery['controls'],
): boolean => {
  if (_.isEmpty(selectedControls)) return false;

  const libraryIdToLinkedScenarios = new Map<string, Set<string>>();

  rmpControls.forEach((control) => {
    const linkedScenarioIds = control.riskManagementPlans.map((rmp) => rmp.scenario?.id).filter(Boolean) as string[];
    const linkedSet = new Set(linkedScenarioIds);

    // Map all framework control identifiers to the linked scenarios
    control.frameworkControls.forEach((fc) => {
      // Map by framework control id (which is library ux_id)
      libraryIdToLinkedScenarios.set(fc.id, linkedSet);

      // Also map by control_identifier (like "8.13")
      if (fc.control_identifier) {
        libraryIdToLinkedScenarios.set(fc.control_identifier, linkedSet);
      }
    });

    // Also map by RMP control id
    libraryIdToLinkedScenarios.set(control.id, linkedSet);
  });

  for (const [libraryControlId, scenarios] of Object.entries(selectedControls)) {
    const existingScenarios = libraryIdToLinkedScenarios.get(libraryControlId) || new Set();

    for (const [scenarioId, isSelected] of Object.entries(scenarios)) {
      if (isSelected && !existingScenarios.has(scenarioId)) {
        return true;
      }
    }
  }

  return false;
};

type ControlScenarioGroup = {
  controlId: string;
  scenarioIds: string[];
};

type GetControlScenarioGroups = (params: {
  controls: ControlsRmpQuery['controls'];
  frameworkControlScenarioLinks: FrameworkControlScenarioInput[];
  selectedControls: Record<string, Record<string, boolean>>;
}) => {
  existingRmpScenarioGroups: ControlScenarioGroup[];
  newControlScenarioGroups: ControlScenarioGroup[];
};

export const getControlScenarioGroups: GetControlScenarioGroups = ({
  controls,
  frameworkControlScenarioLinks,
  selectedControls,
}) => {
  const existingRmpScenarioGroups: ControlScenarioGroup[] = [];
  const newControlScenarioGroups: ControlScenarioGroup[] = [];

  // Get existing RMP control IDs
  const existingControlIds = new Set(controls.map((control) => control.id));

  // Create a mapping from library control identifiers to RMP control ID
  // This maps both framework control id AND control_identifier to the RMP control ID
  const libraryIdToRmpId = new Map<string, string>();
  controls.forEach((control) => {
    control.frameworkControls.forEach((fc) => {
      if (fc.control_identifier) {
        libraryIdToRmpId.set(fc.control_identifier, control.id);
      }
      // Also map by framework control id (which is the library ux_id)
      libraryIdToRmpId.set(fc.id, control.id);
    });
  });

  // Track which RMP controls we've added scenarios to (to avoid duplicates)
  const rmpControlScenarios = new Map<string, Set<string>>();

  // Initialize with existing linked scenarios for each RMP control
  controls.forEach((control) => {
    const existingScenarioIds = control.riskManagementPlans.map((rmp) => rmp.scenario?.id).filter(Boolean) as string[];
    rmpControlScenarios.set(control.id, new Set(existingScenarioIds));
  });

  // Process selectedControls - check if they map to existing RMP controls
  for (const [libraryControlId, scenarios] of Object.entries(selectedControls)) {
    const mappedRmpId = libraryIdToRmpId.get(libraryControlId);

    if (mappedRmpId) {
      // This library control maps to an existing RMP control - add scenarios to existing
      const existingScenarios = rmpControlScenarios.get(mappedRmpId) || new Set();

      for (const [scenarioId, isSelected] of Object.entries(scenarios)) {
        if (isSelected && !existingScenarios.has(scenarioId)) {
          existingScenarios.add(scenarioId);
        }
      }

      rmpControlScenarios.set(mappedRmpId, existingScenarios);
    } else {
      // This is a truly new control - no existing RMP control found
      const scenarioIds: string[] = [];

      for (const [scenarioId, isSelected] of Object.entries(scenarios)) {
        if (isSelected) {
          scenarioIds.push(scenarioId);
        }
      }

      if (scenarioIds.length > 0) {
        newControlScenarioGroups.push({
          controlId: libraryControlId,
          scenarioIds,
        });
      }
    }
  }

  // Also process frameworkControlScenarioLinks (for any that weren't in selectedControls)
  for (const link of frameworkControlScenarioLinks) {
    const mappedRmpId = libraryIdToRmpId.get(link.frameworkControlId);

    if (mappedRmpId) {
      const existingScenarios = rmpControlScenarios.get(mappedRmpId) || new Set();
      if (!existingScenarios.has(link.scenarioId)) {
        existingScenarios.add(link.scenarioId);
        rmpControlScenarios.set(mappedRmpId, existingScenarios);
      }
    }
  }

  // Convert rmpControlScenarios to existingRmpScenarioGroups
  // Only include scenarios that are NEW (not already linked)
  for (const control of controls) {
    const allScenarios = rmpControlScenarios.get(control.id) || new Set();
    const existingLinkedScenarios = new Set(
      control.riskManagementPlans.map((rmp) => rmp.scenario?.id).filter(Boolean) as string[],
    );

    // Find new scenarios to link (not already linked)
    const newScenarioIds = [...allScenarios].filter((id) => !existingLinkedScenarios.has(id));

    if (newScenarioIds.length > 0) {
      existingRmpScenarioGroups.push({
        controlId: control.id,
        scenarioIds: newScenarioIds,
      });
    }
  }

  return {
    existingRmpScenarioGroups,
    newControlScenarioGroups,
  };
};
